<template>
  <div class="chat-widget">
    <!-- Chat Toggle Button -->
    <button 
      @click="toggleChat" 
      class="chat-widget__toggle"
      :aria-expanded="isOpen"
      aria-label="Toggle chat widget"
    >
      <span v-if="!isOpen">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      </span>
      <span v-else>&times;</span>
    </button>

    <!-- Modal Backdrop -->
    <div 
      v-show="isOpen" 
      class="chat-widget__backdrop"
      @click="closeChat"
    ></div>

    <!-- Chat Modal -->
    <div 
      v-show="isOpen" 
      class="chat-widget__modal"
    >
      <div class="chat-widget__modal-content">
        <!-- Modal Header -->
        <div class="chat-widget__header">
          <div class="chat-widget__header-content">
            <h3 class="chat-widget__title">Knowledge Base Assistant</h3>
            <p class="chat-widget__subtitle">Ask me anything about our articles</p>
          </div>
          <button @click="closeChat" class="chat-widget__close-button">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Messages Container -->
        <div class="chat-widget__messages" ref="messagesContainer">
          <div 
            v-for="(message, index) in messages" 
            :key="index"
            class="chat-widget__message"
            :class="{ 'chat-widget__message--user': message.isUser }"
          >
            <div class="chat-widget__message-content">
              <template v-for="(block, blockIndex) in parseMessage(message.text)" :key="blockIndex">
                <h3 v-if="block.type === 'header'" class="chat-header">{{ block.text }}</h3>
                <div v-else-if="block.type === 'bullet'" class="chat-bullet">
                  <span>• </span>
                  <template v-for="(token, tokenIndex) in block.tokens" :key="tokenIndex">
                    <strong v-if="token.type === 'bold'">{{ token.text }}</strong>
                    <a
                      v-else-if="token.type === 'link' && isSafeUrl(token.href)"
                      :href="token.href"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="chat-link"
                    >{{ token.text }}</a>
                    <span v-else-if="token.type === 'link'">{{ token.text }}</span>
                    <template v-else>{{ token.text }}</template>
                  </template>
                </div>
                <p v-else class="chat-paragraph">
                  <template v-for="(token, tokenIndex) in block.tokens" :key="tokenIndex">
                    <br v-if="token.type === 'break'">
                    <strong v-else-if="token.type === 'bold'">{{ token.text }}</strong>
                    <a
                      v-else-if="token.type === 'link' && isSafeUrl(token.href)"
                      :href="token.href"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="chat-link"
                    >{{ token.text }}</a>
                    <span v-else-if="token.type === 'link'">{{ token.text }}</span>
                    <template v-else>{{ token.text }}</template>
                  </template>
                </p>
              </template>
            </div>
          </div>
          <div v-if="isLoading" class="chat-widget__message chat-widget__message--loading">
            <div class="chat-widget__message-content">
              <span class="typing-indicator"></span>
              <span class="loading-text">Searching knowledge base...</span>
            </div>
          </div>
        </div>

        <!-- Input Form -->
        <form @submit.prevent="sendMessage" class="chat-widget__form">
          <input
            v-model="newMessage"
            type="text"
            placeholder="Ask a question about our knowledge base..."
            :disabled="isLoading"
            class="chat-widget__input"
            ref="messageInput"
          >
          <button 
            type="submit" 
            :disabled="isLoading || !newMessage.trim()"
            class="chat-widget__send"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
            </svg>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ChatWidget',
  data() {
    return {
      isOpen: false,
      messages: [],
      newMessage: '',
      isLoading: false
    };
  },
  methods: {
    toggleChat() {
      this.isOpen = !this.isOpen;
      if (this.isOpen && this.messages.length === 0) {
        this.messages.push({
          text: 'Hi! How can I help you with our knowledge base today?',
          isUser: false
        });
        // Focus the input when opening
        this.$nextTick(() => {
          if (this.$refs.messageInput) {
            this.$refs.messageInput.focus();
          }
        });
      }
    },
    closeChat() {
      this.isOpen = false;
    },
    async sendMessage() {
      if (!this.newMessage.trim() || this.isLoading) return;

      const userMessage = this.newMessage.trim();
      this.messages.push({
        text: userMessage,
        isUser: true
      });
      this.newMessage = '';
      this.isLoading = true;

      try {
        const response = await axios.post('/api/v1/chat/send', {
          message: userMessage
        });

        if (response.data.success) {
          this.messages.push({
            text: response.data.message,
            isUser: false
          });
        } else {
          throw new Error('Failed to get response');
        }
      } catch (error) {
        this.messages.push({
          text: 'Sorry, I encountered an error. Please try again.',
          isUser: false
        });
        console.error('Chat error:', error);
      } finally {
        this.isLoading = false;
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },
    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
      }
    },
    parseMessage(text) {
      if (!text) return [];

      const lines = String(text).split('\n');
      const blocks = [];
      let paragraphLines = [];

      const flushParagraph = () => {
        if (paragraphLines.length === 0) return;
        blocks.push({
          type: 'paragraph',
          tokens: this.tokenizeInline(paragraphLines.join('\n')),
        });
        paragraphLines = [];
      };

      for (const line of lines) {
        const headerMatch = /^## (.+)$/.exec(line);
        const bulletMatch = /^- (.+)$/.exec(line);

        if (headerMatch) {
          flushParagraph();
          blocks.push({ type: 'header', text: headerMatch[1] });
        } else if (bulletMatch) {
          flushParagraph();
          blocks.push({
            type: 'bullet',
            tokens: this.tokenizeInline(bulletMatch[1]),
          });
        } else {
          paragraphLines.push(line);
        }
      }
      flushParagraph();

      return blocks;
    },
    tokenizeInline(text) {
      // Tokenize inline markdown: **bold**, [text](url), and newline breaks.
      // Returns plain data objects; rendering uses text interpolation so no
      // HTML is injected and Vue auto-escapes everything.
      const tokens = [];
      const pattern = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)|\n/g;
      let lastIndex = 0;
      let match;

      while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
          tokens.push({ type: 'text', text: text.slice(lastIndex, match.index) });
        }

        if (match[0] === '\n') {
          tokens.push({ type: 'break' });
        } else if (match[1] !== undefined) {
          tokens.push({ type: 'bold', text: match[1] });
        } else if (match[2] !== undefined) {
          tokens.push({ type: 'link', text: match[2], href: match[3] });
        }

        lastIndex = pattern.lastIndex;
      }

      if (lastIndex < text.length) {
        tokens.push({ type: 'text', text: text.slice(lastIndex) });
      }

      return tokens;
    },
    isSafeUrl(href) {
      if (typeof href !== 'string') return false;
      const trimmed = href.trim();
      // Allow only http(s), mailto, tel, and relative URLs. Block javascript:,
      // data:, vbscript:, and other schemes that can execute code.
      return /^(https?:\/\/|mailto:|tel:|\/|#|\?)/i.test(trimmed);
    },
    handleEscapeKey(event) {
      if (event.key === 'Escape' && this.isOpen) {
        this.closeChat();
      }
    }
  },
  watch: {
    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      },
      deep: true
    },
    isOpen(newVal) {
      if (newVal) {
        document.addEventListener('keydown', this.handleEscapeKey);
        document.body.style.overflow = 'hidden'; // Prevent background scrolling
      } else {
        document.removeEventListener('keydown', this.handleEscapeKey);
        document.body.style.overflow = ''; // Restore scrolling
      }
    }
  },
  beforeUnmount() {
    // Clean up event listeners and restore body scroll
    document.removeEventListener('keydown', this.handleEscapeKey);
    document.body.style.overflow = '';
  }
};
</script>

<style scoped>
/* Chat Widget Container */
.chat-widget {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1000;
}

/* Toggle Button */
.chat-widget__toggle {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background-color: #4f46e5;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
  z-index: 1001;
}

.chat-widget__toggle:hover {
  transform: scale(1.05);
  background-color: #3730a3;
}

/* Modal Backdrop */
.chat-widget__backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1002;
  animation: fadeIn 0.2s ease-out;
}

/* Modal Container */
.chat-widget__modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1003;
  padding: 1rem;
}

/* Modal Content */
.chat-widget__modal-content {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 600px;
  height: 80vh;
  max-height: 700px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideUp 0.3s ease-out;
}

/* Modal Header */
.chat-widget__header {
  padding: 1.5rem;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.chat-widget__header-content {
  flex: 1;
}

.chat-widget__title {
  margin: 0 0 0.25rem 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.chat-widget__subtitle {
  margin: 0;
  font-size: 0.875rem;
  opacity: 0.9;
}

.chat-widget__close-button {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.375rem;
  transition: background-color 0.2s;
  margin-left: 1rem;
}

.chat-widget__close-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Messages Area */
.chat-widget__messages {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: #f8fafc;
}

.chat-widget__message {
  display: flex;
  margin-bottom: 0.5rem;
}

.chat-widget__message--user {
  justify-content: flex-end;
}

.chat-widget__message-content {
  max-width: 85%;
  padding: 0.875rem 1.125rem;
  border-radius: 1rem;
  background-color: white;
  word-wrap: break-word;
  line-height: 1.5;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

.chat-widget__message--user .chat-widget__message-content {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
}

.chat-widget__message--loading .chat-widget__message-content {
  background-color: #e2e8f0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Loading Indicator */
.typing-indicator {
  display: inline-block;
  position: relative;
  width: 30px;
  height: 10px;
}

.typing-indicator::after {
  content: '...';
  position: absolute;
  animation: typing 1.5s infinite;
  font-weight: bold;
}

.loading-text {
  font-size: 0.875rem;
  color: #64748b;
}

/* Input Form */
.chat-widget__form {
  padding: 1.5rem;
  border-top: 1px solid #e2e8f0;
  background: white;
  display: flex;
  gap: 0.75rem;
}

.chat-widget__input {
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.75rem;
  outline: none;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.chat-widget__input:focus {
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.chat-widget__send {
  padding: 0.75rem;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
  border: none;
  border-radius: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s, opacity 0.2s;
  min-width: 3rem;
}

.chat-widget__send:hover:not(:disabled) {
  transform: scale(1.05);
}

.chat-widget__send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Markdown Formatting */
.chat-header {
  font-size: 1.1em;
  font-weight: 600;
  color: #1f2937;
  margin: 12px 0 8px 0;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 4px;
}

.chat-link {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px solid transparent;
  transition: all 0.2s ease;
}

.chat-link:hover {
  color: #1d4ed8;
  border-bottom-color: #3b82f6;
}

.chat-bullet {
  margin: 4px 0;
  padding-left: 8px;
}

.chat-paragraph {
  margin: 0;
}

.chat-paragraph + .chat-paragraph {
  margin-top: 8px;
}

.chat-widget__message--user .chat-header {
  color: white;
  border-bottom-color: rgba(255, 255, 255, 0.3);
}

.chat-widget__message--user .chat-link {
  color: #bfdbfe;
}

.chat-widget__message--user .chat-link:hover {
  color: white;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { 
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to { 
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes typing {
  0%, 20% { content: '.'; }
  40% { content: '..'; }
  60% { content: '...'; }
  80%, 100% { content: ''; }
}

/* Responsive Design */
@media (max-width: 768px) {
  .chat-widget__modal {
    padding: 0.5rem;
  }
  
  .chat-widget__modal-content {
    height: 90vh;
    border-radius: 0.75rem;
  }
  
  .chat-widget__header {
    padding: 1rem;
  }
  
  .chat-widget__messages {
    padding: 1rem;
  }
  
  .chat-widget__form {
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .chat-widget__modal {
    padding: 0;
  }
  
  .chat-widget__modal-content {
    height: 100vh;
    border-radius: 0;
    max-height: none;
  }
}
</style>
